import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";

export function OwnerSetup() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async (recoveryEmail: string) => {
      return api<{ redirect: string }>("/auth/owner-setup/email", {
        method: "POST",
        body: JSON.stringify({ email: recoveryEmail }),
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
      window.location.href = data.redirect;
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError(String(err));
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    submit.mutate(email.trim());
  };

  const canSubmit = ack && email.trim().length > 0 && !submit.isPending;

  return (
    <main className="owner-setup">
      <h1>Recovery email</h1>
      <p>
        Record an email address you can reach if you lose your ESI login. This
        is the last step before the server is usable.
      </p>
      <section className="owner-setup__privacy">
        <h2>How we use your recovery email</h2>
        <p>
          This email address will be used <strong>only</strong> to help you
          regain access to this server if you lose your ESI login or are
          otherwise locked out of your owner account.
        </p>
        <p>
          We will <strong>never</strong>:
        </p>
        <ul>
          <li>sell, rent, or share this email with anyone;</li>
          <li>add it to any mailing list, newsletter, or marketing list;</li>
          <li>
            use it for analytics, advertising, profiling, or any purpose other
            than account recovery;
          </li>
          <li>transmit it to any third party or external service.</li>
        </ul>
        <p>
          Your email is stored only in this server's local database. If you ever
          want it removed, contact the operator of this deployment.
        </p>
      </section>
      <form onSubmit={onSubmit} className="owner-setup__form">
        <label htmlFor="owner-email">Recovery email</label>
        <input
          id="owner-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="owner-setup__email"
        />
        <label className="owner-setup__ack">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          I have read the notice above and want to record this address.
        </label>
        <button
          type="submit"
          disabled={!canSubmit}
          className="owner-setup__submit"
        >
          {submit.isPending ? "Saving…" : "Save and continue"}
        </button>
      </form>
      {error && (
        <div className="owner-setup__error" role="alert">
          {error}
        </div>
      )}
    </main>
  );
}
